import { loadGsap } from '@/utils/gsap';

// Directional scroll-assist for opt-in sections (`data-snap`).
//
// When settled on a section (its top at the viewport top) a small scroll nudge
// (>= THRESHOLD px) quickly animates the NEXT/PREVIOUS section to the top, then
// releases — the user scrolls freely afterwards until they settle again.
//
// Only engages when BOTH the current and target sections fit the viewport
// (height <= window height) and the target is roughly the next screenful — so
// long content, odd window sizes, and pinned sections (Truth/Dare, TextCarousel)
// are left to scroll naturally.
//
// Opt-in only, desktop only, off under reduced-motion.

const DESKTOP_QUERY = '(min-width: 1024px)';
const REDUCED_MOTION = window.matchMedia(
	'(prefers-reduced-motion: reduce)'
).matches;

const THRESHOLD = 20; // px of scroll past a section's top before it assists
const SETTLE_TOL = 8; // px tolerance for a section top being "at the top"
const IDLE_MS = 120; // scroll-stop debounce before re-settling
const GAP_FACTOR = 1.25; // skip if the target is further than this × viewport away
const DURATION = 0.45; // assist scroll duration (s)

async function ScrollSnap() {
	if (REDUCED_MOTION) return () => {};
	if (!window.matchMedia(DESKTOP_QUERY).matches) return () => {};

	const sections = [...document.querySelectorAll('[data-snap]')];
	if (sections.length < 2) return () => {};

	try {
		const { gsap } = await loadGsap();

		let settled = null; // the section currently aligned to the top
		let animating = false;
		let idleTimer = null;
		let scrollRaf = null;

		const fits = section =>
			section.getBoundingClientRect().height <= window.innerHeight + 1;
		const topOf = section =>
			section.getBoundingClientRect().top + window.scrollY;

		function snapTo(y) {
			animating = true;
			settled = null;
			const proxy = { y: window.scrollY };
			gsap.to(proxy, {
				y,
				duration: DURATION,
				ease: 'power2.inOut',
				overwrite: true,
				onUpdate: () => window.scrollTo(0, proxy.y),
				onComplete: () => {
					animating = false;
				},
			});
		}

		function attemptSnap(dir) {
			const idx = sections.indexOf(settled);
			const target = sections[idx + dir];
			if (!target || !fits(settled) || !fits(target)) return;
			// Don't leap across a pin or a long run of non-snap content.
			if (
				Math.abs(topOf(target) - topOf(settled)) >
				window.innerHeight * GAP_FACTOR
			) {
				return;
			}
			snapTo(topOf(target));
		}

		function onFrame() {
			scrollRaf = null;
			if (animating || !settled) return;
			const delta = -settled.getBoundingClientRect().top; // down = positive
			if (delta > THRESHOLD) attemptSnap(1);
			else if (delta < -THRESHOLD) attemptSnap(-1);
		}

		// Re-settle once scrolling stops on a section whose top is at the viewport
		// top (covers both the assist landing and a natural free-scroll stop).
		function settle() {
			if (animating) return;
			settled =
				sections.find(
					s => Math.abs(s.getBoundingClientRect().top) <= SETTLE_TOL
				) || null;
		}

		function onScroll() {
			if (!scrollRaf) scrollRaf = requestAnimationFrame(onFrame);
			clearTimeout(idleTimer);
			idleTimer = setTimeout(settle, IDLE_MS);
		}

		// Hold the wheel/touch during the brief assist so it isn't fought.
		function lock(event) {
			if (animating) event.preventDefault();
		}

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('wheel', lock, { passive: false });
		window.addEventListener('touchmove', lock, { passive: false });
		settle(); // initial state (a section may already be at the top)

		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('wheel', lock);
			window.removeEventListener('touchmove', lock);
			clearTimeout(idleTimer);
			if (scrollRaf) cancelAnimationFrame(scrollRaf);
			gsap.killTweensOf(window);
		};
	} catch (error) {
		console.error('ScrollSnap: Failed to load GSAP', error);
		return () => {};
	}
}

export default ScrollSnap;
